import { useEffect, useState } from 'react';

// Fixed constants — not authorable (parent §12.4, §2.2).
const TIMEZONE = 'America/New_York';
const DAY_START_SECONDS = 7 * 3600; // 07:00:00 ET, inclusive (DAY_START)
const DAY_END_SECONDS = 18 * 3600 + 59 * 60 + 59; // 18:59:59 ET, inclusive (DAY_END)

/**
 * Returns the ET wall-clock time of `date` as total seconds since midnight
 * (h*3600 + m*60 + s), normalised to America/New_York with hourCycle h23.
 *
 * @param {Date} date
 * @returns {number}
 */
const getETSeconds = (date) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: TIMEZONE,
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hourCycle: 'h23',
    }).formatToParts(date);

    let h = 0;
    let m = 0;
    let s = 0;
    for (const { type, value } of parts) {
        if (type === 'hour') h = parseInt(value, 10);
        if (type === 'minute') m = parseInt(value, 10);
        if (type === 'second') s = parseInt(value, 10);
    }
    return h * 3600 + m * 60 + s;
};

/**
 * Compute the DAY/NIGHT phase from the given instant normalized to America/New_York.
 * DAY = 07:00:00–18:59:59 ET (closed interval); NIGHT = everything outside that window.
 *
 * @param {Date} [date=new Date()] - The instant to evaluate; defaults to now.
 * @returns {'day' | 'night'}
 */
const compute = (date = new Date()) => {
    const totalSeconds = getETSeconds(date);
    return totalSeconds >= DAY_START_SECONDS && totalSeconds <= DAY_END_SECONDS ? 'day' : 'night';
};

/**
 * Returns ms from `now` to the next phase-change instant in America/New_York:
 * either 07:00:00 ET (Night→Day) or 19:00:00 ET (Day→Night).
 *
 * Targets the exact UTC millisecond of the boundary (not just a whole-second
 * approximation) and corrects for DST transitions that fall within the window
 * by verifying the estimated boundary with a second Intl call and applying a
 * ±1 h adjustment when needed.
 *
 * @param {Date} now - The reference instant (same snapshot used by compute()).
 * @returns {number} Milliseconds until the next boundary.
 */
const msUntilNextBoundary = (now) => {
    const nowMs = now.getTime();
    const currentSeconds = getETSeconds(now);

    let etSecondsToNext;
    if (currentSeconds < DAY_START_SECONDS) {
        // Before 07:00:00 ET — next boundary is DAY_START
        etSecondsToNext = DAY_START_SECONDS - currentSeconds;
    } else if (currentSeconds <= DAY_END_SECONDS) {
        // 07:00:00–18:59:59 ET — next boundary is 19:00:00 ET (DAY_END + 1 s)
        etSecondsToNext = DAY_END_SECONDS + 1 - currentSeconds;
    } else {
        // 19:00:00–23:59:59 ET — next boundary is tomorrow's 07:00:00 ET
        etSecondsToNext = 86400 - currentSeconds + DAY_START_SECONDS;
    }

    // Target the exact boundary second by subtracting now's sub-second remainder.
    const estimatedBoundaryMs = nowMs + etSecondsToNext * 1000 - now.getMilliseconds();

    // Verify the estimate actually falls on the expected ET boundary.
    // If a DST transition falls in the window the ET hour at the estimated UTC
    // instant will be off by ±1 h; the correction below fixes it.
    const expectedBoundarySeconds =
        currentSeconds < DAY_START_SECONDS || currentSeconds > DAY_END_SECONDS
            ? DAY_START_SECONDS // 07:00:00
            : DAY_END_SECONDS + 1; // 19:00:00
    const actualBoundarySeconds = getETSeconds(new Date(estimatedBoundaryMs));
    const dstCorrectionMs = (expectedBoundarySeconds - actualBoundarySeconds) * 1000;

    return estimatedBoundaryMs + dstCorrectionMs - nowMs;
};

/**
 * useTimeOfDay — boundary-tracking ET day/night gate.
 *
 * Returns 'day' | 'night' | null.
 * - null: before the on-mount effect has run (suppress-until-mount signal, parent D-05).
 * - 'day': current ET clock falls in 07:00:00–18:59:59 (parent AC-01).
 * - 'night': current ET clock falls outside that window (parent AC-02).
 *
 * On mount, computes the phase and arms a single setTimeout to fire at the next
 * 07:00:00 or 19:00:00 ET boundary. When the timer fires, phase updates and a new
 * timer is armed for the subsequent boundary (D-06). Cleanup cancels the pending
 * timer on unmount. useState(null)'s initial value ensures SSR and first client
 * paint are identical — hydration-mismatch warning is structurally impossible
 * (parent AC-14 design guarantee).
 */
const useTimeOfDay = () => {
    const [phase, setPhase] = useState(null);

    useEffect(() => {
        let timerId;
        const tick = () => {
            const now = new Date();
            setPhase(compute(now));
            timerId = setTimeout(tick, msUntilNextBoundary(now));
        };
        tick();
        return () => clearTimeout(timerId);
    }, []);

    return phase;
};

export default useTimeOfDay;
